import { useMemo, useState } from 'react'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isBefore,
  isSameDay,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { parseISODate, toISODateString, todayISODate } from '../utils/courseSchedule'

type PickPhase = 'start' | 'end'

interface RangeCalendarPickerProps {
  startDate: string
  endDate: string
  minDate?: string
  onChange: (startDate: string, endDate: string) => void
}

/** Monday = first column (common for scheduling UIs). */
function leadingEmptyCells(monthStart: Date): number {
  return (getDay(monthStart) + 6) % 7
}

function buildMonthCells(viewMonth: Date): (Date | null)[] {
  const monthStart = startOfMonth(viewMonth)
  const monthEnd = endOfMonth(viewMonth)
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd })
  const padStart = leadingEmptyCells(monthStart)
  const cells: (Date | null)[] = [...Array(padStart).fill(null), ...days]
  while (cells.length % 7 !== 0) {
    cells.push(null)
  }
  return cells
}

export default function RangeCalendarPicker({
  startDate,
  endDate,
  minDate = todayISODate(),
  onChange,
}: RangeCalendarPickerProps) {
  const min = useMemo(() => startOfDay(parseISO(minDate)), [minDate])
  const start = startDate ? parseISODate(startDate) : null
  const end = endDate ? parseISODate(endDate) : null

  const initialMonth = start ?? min
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(initialMonth))
  const [phase, setPhase] = useState<PickPhase>(() =>
    startDate && !endDate ? 'end' : 'start',
  )

  const monthCells = useMemo(() => buildMonthCells(viewMonth), [viewMonth])

  const handleDayClick = (day: Date) => {
    const dayStart = startOfDay(day)
    if (isBefore(dayStart, min)) return

    const iso = toISODateString(dayStart)

    if (phase === 'start' || !startDate) {
      onChange(iso, '')
      setPhase('end')
      return
    }

    const startParsed = parseISODate(startDate)
    if (!startParsed) {
      onChange(iso, '')
      setPhase('end')
      return
    }

    if (isBefore(dayStart, startParsed)) {
      onChange(iso, '')
      setPhase('end')
      return
    }

    onChange(startDate, iso)
    setPhase('start')
  }

  const isDisabled = (day: Date) => isBefore(startOfDay(day), min)

  const isRangeStart = (day: Date) => start && isSameDay(day, start)
  const isRangeEnd = (day: Date) => end && isSameDay(day, end)
  const isInRange = (day: Date) => {
    if (!start || !end) return false
    return isWithinInterval(startOfDay(day), {
      start: startOfDay(start),
      end: startOfDay(end),
    })
  }

  const isToday = (day: Date) => isSameDay(day, new Date())

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => setViewMonth((m) => subMonths(m, 1))}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200/80 bg-white text-gray-600 shadow-sm transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="text-center">
          <p className="text-lg font-semibold tracking-tight text-gray-900">
            {format(viewMonth, 'MMMM yyyy')}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {phase === 'end' && startDate
              ? 'Tap the end date'
              : 'Tap the start date'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200/80 bg-white text-gray-600 shadow-sm transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {monthCells.map((day, index) => {
          if (!day) {
            return <div key={`empty-${index}`} className="h-10" aria-hidden />
          }

          const disabled = isDisabled(day)
          const selectedStart = isRangeStart(day)
          const selectedEnd = isRangeEnd(day)
          const inRange = isInRange(day) && !selectedStart && !selectedEnd
          const today = isToday(day)

          return (
            <button
              key={toISODateString(day)}
              type="button"
              disabled={disabled}
              onClick={() => handleDayClick(day)}
              className={[
                'relative h-10 w-full rounded-xl text-sm font-semibold transition-all duration-150',
                disabled
                  ? 'cursor-not-allowed text-gray-300 bg-gray-50/80'
                  : 'text-gray-800 hover:scale-[1.03] active:scale-[0.98]',
                selectedStart || selectedEnd
                  ? 'bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-md shadow-primary-600/30'
                  : inRange
                    ? 'bg-primary-100/90 text-primary-900'
                    : !disabled
                      ? 'hover:bg-primary-50 bg-white border border-gray-100'
                      : '',
                today && !selectedStart && !selectedEnd
                  ? 'ring-2 ring-primary-400/70 ring-offset-1'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {format(day, 'd')}
            </button>
          )
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-gray-500 border-t border-gray-100 pt-4">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-md bg-gradient-to-br from-primary-500 to-primary-700" />
          Selected
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-md bg-primary-100 border border-primary-200" />
          In range
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-md ring-2 ring-primary-400/70" />
          Today
        </span>
      </div>
    </div>
  )
}
